import React from "react";
import { SlidersHorizontal } from "lucide-react";

const inputStyle = {
    background: "var(--c-panel-input)",
    backdropFilter: "blur(3.98905px)",
    boxShadow: "0px 0.99px 6px 0px #0000000F, 0px 0px 4px 0px #FFFFFF inset",
    border: "1px solid var(--c-line-input)",
};

/**
 * Responsive search bar for property pages.
 * Mobile: stacked layout (input full-width, then Filter + action button below).
 * Desktop: single row with overlay filter + action button.
 */
const PropertySearchBar = ({
    value,
    onChange,
    onSearchClick,
    onFilterClick,
    showFilters,
    placeholder = "Search Address or Property Features with AI",
    placeholderMobile = "Search address or features",
    searchButtonLabel = "Search",
    activeFilterCount = 0, // >0 shows a badge on the filter button so it's clear filters are applied
}) => {
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (typeof onSearchClick === "function") onSearchClick();
        }
    };
    return (
        <div className="relative w-full">
            {/* Mobile: stacked layout */}
            <div className="flex flex-col gap-3 sm:hidden">
                <input
                    type="text"
                    placeholder={placeholderMobile}
                    style={inputStyle}
                    className="w-full rounded-2xl sm:rounded-full py-3.5 sm:py-5 pl-4 sm:pl-8 pr-4 text-sm text-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] outline-none transition-all"
                    value={value}
                    onChange={onChange}
                    onKeyDown={handleKeyDown}
                />
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onFilterClick}
                        className={`relative flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-sm transition-all border ${showFilters
                            ? "bg-[#55C5D0] border-[#55C5D0] text-white"
                            : activeFilterCount > 0
                            ? "bg-white border-[#55C5D0] text-[#55C5D0]"
                            : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                            }`}
                    >
                        <SlidersHorizontal className="w-4 h-4" />
                        Filters
                        {activeFilterCount > 0 ? (
                            <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#55C5D0] text-white text-[11px] font-bold leading-none">
                                {activeFilterCount}
                            </span>
                        ) : null}
                    </button>
                    <button
                        type="button"
                        onClick={onSearchClick}
                        className="flex-[2] bg-[#55C5D0] hover:bg-[#4ab3bd] text-white py-3 rounded-2xl font-bold text-sm transition-all shadow-md active:scale-[0.98]"
                    >
                        {searchButtonLabel}
                    </button>
                </div>
            </div>

            {/* Desktop: single row with overlay buttons */}
            <div className="hidden sm:block relative">
                <input
                    type="text"
                    placeholder={placeholder}
                    style={inputStyle}
                    className="w-full rounded-full py-5 pl-8 pr-[11.5rem] md:pr-48 text-gray-600 focus:ring-4 focus:ring-[#55C5D0]/5 focus:border-[#55C5D0] outline-none transition-all"
                    value={value}
                    onChange={onChange}
                    onKeyDown={handleKeyDown}
                />
                <div className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 md:gap-4">
                    <button
                        type="button"
                        onClick={onFilterClick}
                        className={`relative p-2.5 rounded-full shadow-sm transition-all ${showFilters
                            ? "bg-[#55C5D0] text-white"
                            : activeFilterCount > 0
                            ? "bg-white text-[#55C5D0] ring-1 ring-[#55C5D0]"
                            : "bg-white text-black hover:bg-gray-50"}`}
                        aria-label={activeFilterCount > 0 ? `Toggle filters (${activeFilterCount} active)` : "Toggle filters"}
                    >
                        <SlidersHorizontal className="w-5 h-5" />
                        {activeFilterCount > 0 && !showFilters ? (
                            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#55C5D0] text-white text-[11px] font-bold leading-none ring-2 ring-white">
                                {activeFilterCount}
                            </span>
                        ) : null}
                    </button>
                    <button
                        type="button"
                        onClick={onSearchClick}
                        className="bg-[#55C5D0] hover:bg-[#4ab3bd] text-white h-11 px-6 md:px-10 rounded-full font-bold text-sm transition-all shadow-md active:scale-95"
                    >
                        {searchButtonLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PropertySearchBar;
